/**
 * Programa de recomendação (backend lib/ambassador_program.js, 15/10/2026):
 *  - GET  /app/ambassador              → saldo, a receber, extrato de ganhos e de resgates;
 *  - GET  /app/ambassador/slug?slug=   → o identificador do link é válido e está livre?
 *  - POST /app/ambassador/redeem {days}→ soma 1, 7, 31, 92 ou 365 dias à licença.
 * A adesão continua em useAmbassador.ts (POST /app/ambassador).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';

export type EarningStatus = 'pending' | 'available' | 'cancelled';

export interface ReferralEarning {
  id: string;
  paidAt: string;
  availableAt: string;
  cancelledAt: string | null;
  timeCycle: string | null;
  daysContracted: number;
  days: number;
  points: number;
  status: EarningStatus;
}

export interface ReferralRedemption {
  date: string;
  days: number;
  points: number;
  mode: 'license' | 'code';
  tool?: string;
  code?: string;
}

export interface ReferralSummary {
  slug: string;
  discount: number;
  earnPercent: number;
  exchange: number;
  holdDays: number;
  redeemDays: number[];
  balance: {
    availablePoints: number;
    availableDays: number;
    pendingPoints: number;
    pendingDays: number;
    nextAvailableAt: string | null;
  };
  stats: { access: number; purchases: number; earnedDays: number; redeemedDays: number };
  earnings: ReferralEarning[];
  redemptions: ReferralRedemption[];
  license: { expiredAt: string | null; lifetime: boolean };
}

export const referralKey = ['referral'] as const;

export function useReferralSummary(enabled = true) {
  return useQuery({
    queryKey: referralKey,
    enabled,
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await api.get<ReferralSummary & { error?: string }>('app/ambassador', {
          signal,
          rawErrors: true,
        }),
      );
      if (data.error || !data.balance) throw new Error(data.error ?? 'invalid');
      return data as ReferralSummary;
    },
    staleTime: 30_000,
    retry: 1,
  });
}

export interface SlugCheck {
  slug: string;
  valid: boolean;
  available: boolean;
}

export async function checkSlug(slug: string, signal?: AbortSignal): Promise<SlugCheck> {
  const path = `app/ambassador/slug?slug=${encodeURIComponent(slug)}`;
  return unwrap(await api.get<SlugCheck>(path, { signal, rawErrors: true }));
}

export interface RedeemResult {
  success?: boolean;
  error?: 'invalid_period' | 'not_ambassador' | 'lifetime' | 'insufficient_balance' | string;
  days?: number;
  expiredAt?: string;
  summary?: ReferralSummary;
}

/** Soma dias à licença. O `error` do backend volta no resultado, sem virar exceção. */
export function useRedeemDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (days: number): Promise<RedeemResult> =>
      unwrap(await api.post<RedeemResult>('app/ambassador/redeem', { days }, { rawErrors: true })),
    onSuccess: (data) => {
      if (!data.success) return;
      if (data.summary) qc.setQueryData(referralKey, data.summary);
      // A data da licença mudou: o selo da Home e o aviso de expiração vêm do /me.
      void qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });
}
